import { MouseEvent, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Field, FileField, ImageField, TextField } from '@sitecore-jss/sitecore-jss-nextjs';

import { icon_close_medium, icon_play } from '@gn-audio/icons';
import {
  ComponentProps,
  JabraIcon,
  resolveBackgroundStyle,
  resolveColorProperty,
  useFallbackTranslation,
} from '@gn-audio/jss-components';

import { useVideoModal } from 'common/hooks/useVideoModal';
import VideoBackground from 'composables/Banners/VideoBanner/VideoBackground';
import VideoEmbed from 'composables/Product/VideoEmbed/VideoEmbed';
import { useSpotTracking } from 'utils/hooks/useSpotTracking';

type VideoSpotProps = {
  fields: {
    videoLg: FileField;
    videoMd: FileField;
    videoXs: FileField;
    videoLgApple: FileField;
    videoMdApple: FileField;
    videoXsApple: FileField;
    image: ImageField;
    text: TextField;
    textShared: TextField;
    backgroundContrast: Field<number>;
    youtubeVideoID: TextField;
    youkuVideoID: TextField;
    reducedWidth: Field<boolean>;
    hideControls: Field<boolean>;
    unlockAspectRatio: Field<boolean>;
    backgroundColor: TextField;
    backgroundColorShared: TextField;
    textColor: TextField;
    firstCtaButtonTrackingName?: Field<string>;
    trackingSpotName?: Field<string>;
  };
} & ComponentProps;

const supportsHEVCAlpha = () => {
  if (typeof window === 'undefined') return false;

  const ua = window.navigator.userAgent.toLowerCase();

  const isIOS =
    /iphone|ipad|ipod/.test(ua) || (ua.includes('macintosh') && 'ontouchend' in document);

  const isSafari =
    ua.indexOf('safari') !== -1 && ua.indexOf('chrome') === -1 && ua.indexOf('edg') === -1;

  const hasMediaCapabilities = !!(
    navigator.mediaCapabilities && navigator.mediaCapabilities.decodingInfo
  );

  return isIOS || (isSafari && hasMediaCapabilities);
};

const VideoSpot = ({ fields }: VideoSpotProps) => {
  const {
    videoLg,
    videoMd,
    videoXs,
    videoLgApple,
    videoMdApple,
    videoXsApple,
    image,
    backgroundContrast,
    reducedWidth,
    unlockAspectRatio,
    hideControls,
    youtubeVideoID,
    youkuVideoID,
    textColor,
    firstCtaButtonTrackingName,
    trackingSpotName,
  } = fields;

  const spotRef = useSpotTracking(trackingSpotName?.value);

  const [effectiveVideos, setEffectiveVideos] = useState({
    lg: videoLg,
    md: videoMd,
    xs: videoXs,
  });

  const { t } = useFallbackTranslation();

  useLayoutEffect(() => {
    if (typeof window !== 'undefined') {
      const isCompatible = supportsHEVCAlpha();

      if (isCompatible !== null) {
        setEffectiveVideos({
          lg: isCompatible && videoLgApple?.value ? videoLgApple : videoLg,
          md: isCompatible && videoMdApple?.value ? videoMdApple : videoMd,
          xs: isCompatible && videoXsApple?.value ? videoXsApple : videoXs,
        });
      }
    }
  }, [videoLg, videoMd, videoXs, videoLgApple, videoMdApple, videoXsApple]);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const { activeVideoId, setActiveVideoId, closeVideoModal, handleClickOutside } = useVideoModal({
    triggerRef,
    closeButtonRef,
    containerRef,
    modalRef,
  });

  const handleOpenVideo = (event: MouseEvent<HTMLButtonElement>) => {
    triggerRef.current = event.currentTarget;
    setActiveVideoId(youtubeVideoID?.value || youkuVideoID?.value || null);
  };

  const title = fields.text?.value;
  const spotBackgroundStyle = resolveBackgroundStyle(fields?.backgroundColor?.value as string) || {
    backgroundColor: 'transparent',
  };

  const hasVideoSrc = youtubeVideoID?.value || youkuVideoID?.value;

  const textColorStyle = resolveColorProperty(textColor?.value as string) || '#FFFFFF';

  return (
    <div ref={spotRef} data-wt-spot={trackingSpotName?.value} style={spotBackgroundStyle}>
      <VideoBackground
        videoLg={effectiveVideos.lg}
        videoMd={effectiveVideos.md}
        videoXs={effectiveVideos.xs}
        image={image}
        backgroundContrast={backgroundContrast.value}
        reducedWidth={reducedWidth.value}
        hideControls={hideControls?.value}
        unlockAspectRatio={unlockAspectRatio?.value}
        videoContainerClass="aspect-[9/16] md:aspect-video"
      >
        {title && (
          <div
            dangerouslySetInnerHTML={{ __html: title as string }}
            className="mb-6 w-full px-6 text-center text-white md:mb-11"
            style={{ color: textColorStyle }}
          />
        )}
        {hasVideoSrc && (
          <button
            ref={triggerRef}
            onClick={handleOpenVideo}
            aria-label={t('system.OpenPlayer')}
            {...(firstCtaButtonTrackingName?.value && {
              'data-wt-name': firstCtaButtonTrackingName.value,
            })}
            className="bg-primary/20 group flex h-[120px] w-[89px] items-center justify-center rounded-full transition-colors hover:bg-gray-700/20"
          >
            <div className="flex h-[96px] w-[65px] items-center justify-center rounded-full bg-primary text-on-brand transition-colors hover:bg-gray-700 hover:text-white">
              <JabraIcon viewBox="0 0 24 24" className="size-7" Icon={icon_play} />
            </div>
          </button>
        )}

        {activeVideoId &&
          createPortal(
            <div
              ref={modalRef}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
              role="dialog"
              aria-modal="true"
              aria-label={t('system.VideoPlayer')}
            >
              <div onClick={handleClickOutside} className="absolute inset-0" />
              <button
                onFocus={() => {
                  const videoEmbed = modalRef.current?.querySelector('iframe');
                  videoEmbed?.focus();
                }}
                className="sr-only"
                aria-hidden="true"
              />
              <button
                aria-label={t('system.ClosePlayer')}
                ref={closeButtonRef}
                onClick={closeVideoModal}
                className="absolute right-6 top-6 flex size-12 items-center justify-center rounded-full bg-primary text-on-brand md:right-9 md:top-9 md:h-20 md:w-20"
              >
                <JabraIcon Icon={icon_close_medium} />
              </button>
              <div className="relative w-full max-w-[90%] md:max-w-[75%]" ref={containerRef}>
                <VideoEmbed
                  className="aspect-video size-full bg-black"
                  youkuId={youkuVideoID?.value as string}
                  youtubeId={youtubeVideoID?.value as string}
                />
                <button
                  onFocus={() => {
                    closeButtonRef.current?.focus();
                  }}
                  className="sr-only"
                  aria-hidden="true"
                />
              </div>
            </div>,
            document.body
          )}
      </VideoBackground>
    </div>
  );
};

export default VideoSpot;
